import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useSectionAccess } from '../../hooks/useSectionAccess';
import ImportModal from './ImportModal';
import { 
  Search,
  Filter,
  Download,
  Upload,
  Eye,
  EyeOff,
  Plus,
  Edit,
  Trash2,
  UserPlus,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  FileSpreadsheet,
  FileText,
  Copy
} from 'lucide-react';

const Table2 = ({
  data = [],
  columns = [],
  title = "Tabla de Datos",
  subtitle = "",
  pageSize = 50,
  searchable = true,
  filterable = true,
  sortable = true,
  exportable = true,
  importable = true,
  selectable = true,
  showActions = true,
  onAdd,
  onEdit,
  onDelete,
  onImport,
  onAssign,
  onView,
  onDuplicate,
  onRowClick,
  textScale = 1,
  wrapCells = true,
  isDarkMode = false,
  className = "",
  ...props
}) => {
  const theme = useTheme();
  const access = useSectionAccess();
  const addHandler = access.canCreate ? onAdd : undefined;
  const editHandler = access.canUpdate ? onEdit : undefined;
  const deleteHandler = access.canDelete ? onDelete : undefined;
  
  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pageSize);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const initialVisible = new Set(columns.filter(col => !col.hidden).map(col => col.key));
    console.log('🔍 Table2 - Columnas visibles iniciales:', Array.from(initialVisible));
    return initialVisible;
  });
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [columnWidths, setColumnWidths] = useState(() => {
    const widths = {};
    columns.forEach(col => {
      // Usar el ancho especificado en la columna o un valor por defecto
      widths[col.key] = col.width || 150;
    });
    return widths;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRowForActions, setSelectedRowForActions] = useState(null);

  // Función para calcular el ancho óptimo de una columna basado en su contenido
  const calculateOptimalWidth = (columnKey, data) => {
    console.log(`🔍 Table2 - Calculando ancho óptimo para columna: ${columnKey}`);
    const column = columns.find(col => col.key === columnKey);
    if (!column) {
      console.log(`🔍 Table2 - Columna ${columnKey} no encontrada, usando ancho por defecto: 150`);
      return 150;
    }

    // Si la columna tiene un ancho específico definido, usarlo
    if (column.width) {
      console.log(`🔍 Table2 - Columna ${columnKey} tiene ancho específico: ${column.width}`);
      return column.width;
    }

    // Crear un elemento temporal para medir el ancho real del texto
    const measureText = (text, fontSize = '14px', fontFamily = 'system-ui, -apple-system, sans-serif') => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      context.font = `${fontSize} ${fontFamily}`;
      return context.measureText(text).width;
    };

    // Calcular ancho basado en el contenido
    let maxWidth = 100; // Ancho mínimo
    
    // Considerar el título de la columna
    const titleWidth = measureText(column.title || column.label || '') + 20; // +20px para padding
    maxWidth = Math.max(maxWidth, titleWidth);
    console.log(`🔍 Table2 - Columna ${columnKey} - Ancho del título: ${titleWidth}px`);

    // Considerar el contenido de los datos
    data.forEach((row, index) => {
      let cellContent = '';
      
      if (column.render && typeof column.render === 'function') {
        // Para columnas con render personalizado, estimar el ancho basado en el tipo de contenido
        const value = row[columnKey];
        if (typeof value === 'boolean') {
          // Para badges de booleanos (Sí/No), usar ancho fijo
          cellContent = 'Sí'; // El texto más largo
        } else {
          cellContent = String(value || '');
        }
      } else {
        cellContent = String(row[columnKey] || '');
      }
      
      // Calcular ancho real del contenido
      const contentWidth = measureText(cellContent) + 40; // +40px para padding y bordes
      maxWidth = Math.max(maxWidth, contentWidth);
      
      if (index < 3) { // Solo mostrar los primeros 3 para no saturar la consola
        console.log(`🔍 Table2 - Columna ${columnKey} - Fila ${index}: "${cellContent}" (${contentWidth}px)`);
      }
    });

    // Limitar el ancho máximo para evitar columnas demasiado anchas
    const finalWidth = Math.min(maxWidth, 500); // Aumentado de 400 a 500
    console.log(`🔍 Table2 - Columna ${columnKey} - Ancho final: ${finalWidth}px (antes del límite: ${maxWidth}px)`);
    return finalWidth;
  };

  // Actualizar columnas visibles cuando cambien las columnas
  useEffect(() => {
    console.log('🔍 Table2 - Columnas recibidas:', columns);
    const visibleCols = columns.filter(col => !col.hidden).map(col => col.key);
    console.log('🔍 Table2 - Columnas visibles calculadas:', visibleCols);
    console.log('🔍 Table2 - Duplicados en visibleCols:', visibleCols.filter((item, index) => visibleCols.indexOf(item) !== index));
    setVisibleColumns(new Set(visibleCols));
    
    // Actualizar anchos de columnas cuando cambien las columnas
    setColumnWidths(prevWidths => {
      const newWidths = {};
      columns.forEach(col => {
        // Mantener el ancho existente si existe, o usar el nuevo ancho especificado
        newWidths[col.key] = prevWidths[col.key] || col.width || 150;
      });
      console.log('🔍 Table2 - Actualizando anchos de columnas:', newWidths);
      return newWidths;
    });
  }, [columns]);

  // Auto-ajustar anchos de columnas cuando cambien los datos
  useEffect(() => {
    if (data && data.length > 0) {
      console.log('🔍 Table2 - Auto-ajustando anchos de columnas basado en datos...');
      setColumnWidths(prevWidths => {
        const newWidths = {};
        columns.forEach(col => {
          // Si la columna tiene un ancho específico definido, mantenerlo
          if (col.width) {
            newWidths[col.key] = col.width;
          } else {
            // Calcular ancho óptimo basado en el contenido
            newWidths[col.key] = calculateOptimalWidth(col.key, data);
          }
        });
        console.log('🔍 Table2 - Anchos auto-ajustados:', newWidths);
        return newWidths;
      });
    }
  }, [data, columns]);

  // Datos procesados
  const processedData = useMemo(() => {
    let result = [...data];

    // Aplicar búsqueda
    if (searchTerm) {
      result = result.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Aplicar filtros
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        result = result.filter(row =>
          String(row[key]).toLowerCase().includes(filters[key].toLowerCase())
        );
      }
    });

    // Aplicar ordenamiento
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, searchTerm, filters, sortConfig]);

  // Paginación
  const totalPages = Math.ceil(processedData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return processedData.slice(startIndex, startIndex + itemsPerPage);
  }, [processedData, currentPage, itemsPerPage]);

  // Resetear página cuando cambie itemsPerPage
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  // Mantener sincronizado el tamaño de página por defecto.
  // Esto aplica cambios de pageSize también a tablas ya montadas.
  useEffect(() => {
    setItemsPerPage(pageSize);
  }, [pageSize]);

  // Handlers
  const handleSort = useCallback((columnKey) => {
    setSortConfig(prev => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedRows.size === paginatedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginatedData.map((_, index) => index)));
    }
  }, [paginatedData, selectedRows]);

  const handleSelectRow = useCallback((index) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
    
    // Si solo hay una fila seleccionada, la marcamos para acciones
    if (newSelected.size === 1) {
      const selectedIndex = Array.from(newSelected)[0];
      setSelectedRowForActions(paginatedData[selectedIndex]);
    } else {
      setSelectedRowForActions(null);
    }
  }, [selectedRows, paginatedData]);

  const handleExport = useCallback((format) => {
    const exportData = processedData.filter((_, index) => 
      selectedRows.size === 0 || selectedRows.has(index)
    );

    if (format === 'csv') {
      const csv = [
        columns.filter(col => visibleColumns.has(col.key)).map(col => col.title).join(','),
        ...exportData.map(row => 
          columns.filter(col => visibleColumns.has(col.key))
            .map(col => `"${row[col.key] || ''}"`)
            .join(',')
        )
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.toLowerCase().replace(/\s+/g, '_')}.csv`;
      a.click();
    } else if (format === 'json') {
      const json = JSON.stringify(exportData, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.toLowerCase().replace(/\s+/g, '_')}.json`;
      a.click();
    }
  }, [processedData, selectedRows, columns, visibleColumns, title]);

  // Funciones para redimensionamiento de columnas
  const handleMouseDown = useCallback((e, columnKey) => {
    e.preventDefault();
    setIsResizing(true);
    setResizingColumn(columnKey);
    
    const startX = e.clientX;
    const startWidth = columnWidths[columnKey];
    
    const handleMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + (diff * 2)); // Sensibilidad aumentada x2, mínimo 50px
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: newWidth
      }));
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      setResizingColumn(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columnWidths]);

  // Función para forzar el re-ajuste automático de todas las columnas
  const handleAutoResize = useCallback(() => {
    console.log('🔍 Table2 - Forzando re-ajuste automático de columnas...');
    console.log('🔍 Table2 - Datos actuales:', data);
    console.log('🔍 Table2 - Columnas actuales:', columns);
    
    setColumnWidths(prevWidths => {
      console.log('🔍 Table2 - Anchos anteriores:', prevWidths);
      const newWidths = {};
      columns.forEach(col => {
        // Si la columna tiene un ancho específico definido, mantenerlo
        if (col.width) {
          newWidths[col.key] = col.width;
          console.log(`🔍 Table2 - Columna ${col.key} mantiene ancho específico: ${col.width}`);
        } else {
          // Calcular ancho óptimo basado en el contenido
          const optimalWidth = calculateOptimalWidth(col.key, data);
          newWidths[col.key] = optimalWidth;
          console.log(`🔍 Table2 - Columna ${col.key} calculada con ancho óptimo: ${optimalWidth}`);
        }
      });
      console.log('🔍 Table2 - Anchos re-ajustados manualmente:', newWidths);
      return newWidths;
    });
  }, [columns, data, calculateOptimalWidth]);

  // Estilos
  const containerStyles = {
    backgroundColor: theme.colors.primary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: theme.borderRadius.lg,
    overflow: 'hidden',
    boxShadow: theme.colors.shadow.md,
    ...props.style
  };

  const headerStyles = {
    padding: theme.spacing.lg,
    borderBottom: `1px solid ${theme.colors.border.primary}`,
    backgroundColor: theme.colors.secondary,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing.md
  };

  const titleStyles = {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.text.primary,
    margin: 0
  };

  const titleRowStyles = {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing.md,
    flexWrap: 'wrap'
  };

  const subtitleStyles = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.text.secondary,
    margin: 0
  };

  const toolbarStyles = {
    display: 'flex',
    gap: theme.spacing.sm,
    alignItems: 'center',
    flexWrap: 'wrap'
  };

  const searchInputStyles = {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    paddingRight: '40px',
    backgroundColor: theme.colors.primary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: theme.borderRadius.md,
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.text.primary,
    outline: 'none',
    minWidth: '250px',
    transition: 'all 0.2s ease-in-out'
  };

  const buttonStyles = {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: 'transparent',
    border: `1px solid ${isDarkMode ? theme.colors.border.primary : '#d1d5db'}`,
    borderRadius: theme.borderRadius.md,
    color: theme.colors.text.primary,
    fontSize: theme.typography.fontSize.sm,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    transition: 'all 0.2s ease-in-out'
  };

  const primaryButtonStyles = {
    ...buttonStyles,
    backgroundColor: theme.colors.interactive.primary,
    borderColor: theme.colors.interactive.primary,
    color: theme.colors.text.accent
  };

  const tableStyles = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: theme.typography.fontSize.sm
  };

  const thStyles = {
    padding: theme.spacing.md,
    textAlign: 'center',
    backgroundColor: theme.colors.tertiary,
    borderBottom: `2px solid ${theme.colors.border.primary}`,
    color: theme.colors.text.primary,
    fontSize: `${parseFloat(theme.typography.fontSize.sm) * textScale}px`,
    fontWeight: theme.typography.fontWeight.semibold,
    position: 'relative',
    userSelect: 'none',
    cursor: sortable ? 'pointer' : 'default'
  };

  const tdStyles = {
    padding: theme.spacing.md,
    textAlign: 'center', // Cambiado de left a center para centrado por defecto
    borderBottom: `1px solid ${theme.colors.border.primary}`,
    fontSize: `${parseFloat(theme.typography.fontSize.sm) * 0.92 * textScale}px`,
    color: theme.colors.text.primary,
    userSelect: 'none',
    whiteSpace: wrapCells ? 'normal' : 'nowrap',
    overflow: wrapCells ? 'visible' : 'hidden',
    textOverflow: wrapCells ? 'unset' : 'ellipsis',
    wordWrap: wrapCells ? 'break-word' : 'normal',
    maxWidth: wrapCells ? '300px' : 'none'
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === 'asc' ? 
        <ChevronUp size={16} /> : 
        <ChevronDown size={16} />;
    }
    return sortable ? <ChevronsUpDown size={16} /> : null;
  };

  // Debug logs
  console.log('🔍 Table2 - Renderizando tabla con columnas visibles:', Array.from(visibleColumns));
  console.log('🔍 Table2 - Total de columnas:', columns.length);
  console.log('🔍 Table2 - Columnas con hidden:', columns.filter(col => col.hidden).map(col => col.key));
  console.log('🔍 Table2 - Columnas con key status:', columns.filter(col => col.key === 'status'));
  console.log('🔍 Table2 - Columnas filtradas para render:', columns.filter(col => visibleColumns.has(col.key)).map(col => ({ key: col.key, title: col.title })));

  return (
    <div style={containerStyles} className={className}>
      {/* Header */}
      <div style={headerStyles}>
        <div style={titleRowStyles}>
          <h3 style={titleStyles}>{title}</h3>
          {subtitle && <p style={subtitleStyles}>{subtitle}</p>}
        </div>

        {/* Fila de búsqueda */}
        {searchable && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: theme.spacing.md,
            padding: `0 ${theme.spacing.md}`
          }}>
            <div style={{ position: 'relative', width: '300px' }}>
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={searchInputStyles}
                onFocus={(e) => {
                  e.target.style.borderColor = theme.colors.interactive.primary;
                  e.target.style.backgroundColor = theme.colors.hover;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = theme.colors.border.primary;
                  e.target.style.backgroundColor = theme.colors.primary;
                }}
              />
              <Search 
                size={16} 
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: theme.colors.text.muted,
                  pointerEvents: 'none'
                }}
              />
            </div>
          </div>
        )}

        <div style={toolbarStyles}>

          {/* Botón de filtros */}
          {filterable && (
            <button
              style={{
                ...buttonStyles,
                backgroundColor: showFilters ? theme.colors.interactive.primary : 'transparent',
                color: showFilters ? theme.colors.text.accent : theme.colors.text.primary
              }}
              onClick={() => setShowFilters(!showFilters)}
              onMouseEnter={(e) => {
                if (!showFilters) {
                  e.target.style.backgroundColor = theme.colors.hover;
                  e.target.style.borderColor = isDarkMode ? theme.colors.border.primary : '#9ca3af';
                }
              }}
              onMouseLeave={(e) => {
                if (!showFilters) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = isDarkMode ? theme.colors.border.primary : '#d1d5db';
                }
              }}
            >
              <Filter size={16} />
              Filtros
            </button>
          )}

          {/* Selector de columnas */}
          {selectable && (
            <div style={{ position: 'relative' }}>
              <button
                style={{
                  ...buttonStyles,
                  backgroundColor: showColumnSelector ? theme.colors.interactive.primary : 'transparent',
                  color: showColumnSelector ? theme.colors.text.accent : theme.colors.text.primary
                }}
                onClick={() => setShowColumnSelector(!showColumnSelector)}
                onMouseEnter={(e) => {
                  if (!showColumnSelector) {
                    e.target.style.backgroundColor = theme.colors.hover;
                    e.target.style.borderColor = isDarkMode ? theme.colors.border.primary : '#9ca3af';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showColumnSelector) {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = isDarkMode ? theme.colors.border.primary : '#d1d5db';
                  }
                }}
              >
                <Eye size={16} />
                Columnas
              </button>

              {/* Dropdown de columnas */}
              {showColumnSelector && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: theme.spacing.xs,
                  backgroundColor: theme.colors.secondary,
                  border: `1px solid ${theme.colors.border.primary}`,
                  borderRadius: theme.borderRadius.md,
                  boxShadow: theme.colors.shadow.lg,
                  zIndex: 1000,
                  minWidth: '200px',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {columns.map((column) => (
                    <div
                      key={column.key}
                      style={{
                        padding: theme.spacing.sm,
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing.sm,
                        cursor: 'pointer',
                        borderBottom: `1px solid ${theme.colors.border.primary}`
                      }}
                      onClick={() => {
                        const newVisible = new Set(visibleColumns);
                        if (newVisible.has(column.key)) {
                          newVisible.delete(column.key);
                        } else {
                          newVisible.add(column.key);
                        }
                        setVisibleColumns(newVisible);
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = theme.colors.hover;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      {visibleColumns.has(column.key) ? 
                        <Eye size={14} /> : 
                        <EyeOff size={14} />
                      }
                      <span style={{ color: theme.colors.text.primary }}>
                        {column.title}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Exportación */}
          {exportable && (
            <div style={{ position: 'relative' }}>
              {console.log('🔍 Table2 - Renderizando botones de exportación y auto-ajuste')}
              <button
                style={buttonStyles}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.colors.hover;
                  e.target.style.borderColor = isDarkMode ? theme.colors.border.primary : '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = isDarkMode ? theme.colors.border.primary : '#d1d5db';
                }}
              >
                <Download size={16} />
                Exportar
              </button>
              
              
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: theme.spacing.xs,
                backgroundColor: theme.colors.secondary,
                border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: theme.borderRadius.md,
                boxShadow: theme.colors.shadow.lg,
                zIndex: 1000,
                minWidth: '150px',
                display: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.previousSibling.style.backgroundColor = theme.colors.hover;
                e.currentTarget.style.display = 'block';
              }}
              >
                <button
                  style={{
                    width: '100%',
                    padding: theme.spacing.sm,
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: theme.colors.text.primary,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.sm,
                    borderBottom: `1px solid ${theme.colors.border.primary}`
                  }}
                  onClick={() => handleExport('csv')}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme.colors.hover;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <FileSpreadsheet size={14} />
                  CSV
                </button>
                <button
                  style={{
                    width: '100%',
                    padding: theme.spacing.sm,
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: theme.colors.text.primary,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.sm
                  }}
                  onClick={() => handleExport('json')}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme.colors.hover;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <FileText size={14} />
                  JSON
                </button>
              </div>
            </div>
          )}

          {/* Importación */}
          {importable && (
            <button
              style={buttonStyles}
              onClick={() => setShowImportModal(true)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = theme.colors.hover;
                e.target.style.borderColor = isDarkMode ? theme.colors.border.primary : '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = isDarkMode ? theme.colors.border.primary : '#d1d5db';
              }}
            >
              <Upload size={16} />
              Importar
            </button>
          )}

          {/* Botones de Acción */}
          {showActions && (
            <>
              {/* Botón Ver */}
              {onView && (
                <button
                  disabled={!selectedRowForActions}
                  style={{
                    ...buttonStyles,
                    backgroundColor: selectedRowForActions ? '#28a745' : theme.colors.tertiary,
                    borderColor: selectedRowForActions ? '#28a745' : theme.colors.border.secondary,
                    color: selectedRowForActions ? 'white' : theme.colors.text.muted,
                    opacity: selectedRowForActions ? 1 : 0.6,
                    cursor: selectedRowForActions ? 'pointer' : 'not-allowed',
                  }}
                  onClick={() => {
                    if (selectedRowForActions) {
                      const selectedIndex = Array.from(selectedRows)[0];
                      onView(selectedRowForActions, selectedIndex);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (selectedRowForActions) {
                      e.target.style.backgroundColor = '#218838';
                      e.target.style.borderColor = '#218838';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedRowForActions) {
                      e.target.style.backgroundColor = '#28a745';
                      e.target.style.borderColor = '#28a745';
                    }
                  }}
                >
                  <Eye size={16} />
                  Ver
                </button>
              )}

              {/* Botón Agregar */}
              {addHandler && (
                <button
                  style={primaryButtonStyles}
                  onClick={addHandler}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme.colors.interactive.primaryHover;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme.colors.interactive.primary;
                  }}
                >
                  <Plus size={16} />
                  Agregar
                </button>
              )}

              {/* Botón Duplicar */}
              {onDuplicate && (
                <button
                  disabled={selectedRows.size === 0}
                  style={{
                    ...buttonStyles,
                    backgroundColor: selectedRows.size > 0 ? '#007bff' : theme.colors.tertiary,
                    borderColor: selectedRows.size > 0 ? '#007bff' : theme.colors.border.secondary,
                    color: selectedRows.size > 0 ? 'white' : theme.colors.text.muted,
                    opacity: selectedRows.size > 0 ? 1 : 0.6,
                    cursor: selectedRows.size > 0 ? 'pointer' : 'not-allowed'
                  }}
                  onClick={() => {
                    if (selectedRows.size > 0) {
                      const selectedData = Array.from(selectedRows).map(index => processedData[index]);
                      onDuplicate(selectedData, Array.from(selectedRows));
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (selectedRows.size > 0) {
                      e.target.style.backgroundColor = '#0056b3';
                      e.target.style.borderColor = '#0056b3';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedRows.size > 0) {
                      e.target.style.backgroundColor = '#007bff';
                      e.target.style.borderColor = '#007bff';
                    }
                  }}
                >
                  <Copy size={16} />
                  Duplicar
                </button>
              )}

              {/* Botón Editar */}
              {editHandler && (
                <button
                  disabled={!selectedRowForActions}
                  style={{
                    ...buttonStyles,
                    backgroundColor: selectedRowForActions ? theme.colors.interactive.warning + '20' : theme.colors.tertiary,
                    borderColor: selectedRowForActions ? theme.colors.interactive.warning : theme.colors.border.secondary,
                    color: selectedRowForActions ? theme.colors.interactive.warning : theme.colors.text.muted,
                    opacity: selectedRowForActions ? 1 : 0.6,
                    cursor: selectedRowForActions ? 'pointer' : 'not-allowed'
                  }}
                  onClick={() => {
                    if (selectedRowForActions) {
                      const selectedIndex = Array.from(selectedRows)[0];
                      editHandler(selectedRowForActions, selectedIndex);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (selectedRowForActions) {
                      e.target.style.backgroundColor = theme.colors.interactive.warning;
                      e.target.style.color = theme.colors.text.accent;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedRowForActions) {
                      e.target.style.backgroundColor = theme.colors.interactive.warning + '20';
                      e.target.style.color = theme.colors.interactive.warning;
                    }
                  }}
                >
                  <Edit size={16} />
                  Editar
                </button>
              )}

              {/* Botón Asignar */}
              {onAssign && (
                <button
                  disabled={selectedRows.size < 2}
                  style={{
                    ...buttonStyles,
                    backgroundColor: selectedRows.size >= 2 ? theme.colors.interactive.info + '20' : theme.colors.tertiary,
                    borderColor: selectedRows.size >= 2 ? theme.colors.interactive.info : theme.colors.border.secondary,
                    color: selectedRows.size >= 2 ? theme.colors.interactive.info : theme.colors.text.muted,
                    opacity: selectedRows.size >= 2 ? 1 : 0.6,
                    cursor: selectedRows.size >= 2 ? 'pointer' : 'not-allowed'
                  }}
                  onClick={() => {
                    if (selectedRows.size >= 2) {
                      const selectedData = Array.from(selectedRows).map(index => ({
                        data: processedData[index],
                        index: index
                      }));
                      onAssign(selectedData);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (selectedRows.size >= 2) {
                      e.target.style.backgroundColor = theme.colors.interactive.info;
                      e.target.style.color = theme.colors.text.accent;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedRows.size >= 2) {
                      e.target.style.backgroundColor = theme.colors.interactive.info + '20';
                      e.target.style.color = theme.colors.interactive.info;
                    }
                  }}
                >
                  <UserPlus size={16} />
                  Asignar
                </button>
              )}

              {/* Botón Eliminar */}
              {deleteHandler && (
                <button
                  disabled={selectedRows.size === 0}
                  style={{
                    ...buttonStyles,
                    backgroundColor: selectedRows.size > 0 ? theme.colors.interactive.danger + '20' : theme.colors.tertiary,
                    borderColor: selectedRows.size > 0 ? theme.colors.interactive.danger : theme.colors.border.secondary,
                    color: selectedRows.size > 0 ? theme.colors.interactive.danger : theme.colors.text.muted,
                    opacity: selectedRows.size > 0 ? 1 : 0.6,
                    cursor: selectedRows.size > 0 ? 'pointer' : 'not-allowed'
                  }}
                  onClick={() => {
                    if (selectedRows.size > 0) {
                      const selectedData = Array.from(selectedRows).map(index => ({
                        data: data[index],
                        index: index
                      }));
                      
                      const confirmMessage = selectedRows.size === 1 
                        ? `¿Estás seguro de que deseas eliminar este elemento?`
                        : `¿Estás seguro de que deseas eliminar ${selectedRows.size} elementos?`;
                      
                      if (window.confirm(confirmMessage)) {
                        // Si hay múltiples elementos seleccionados, eliminar todos
                        if (selectedRows.size === 1) {
                          // Una sola fila - pasar el objeto directamente
                          deleteHandler(selectedData[0].data, selectedData[0].index);
                        } else {
                          // Múltiples filas - pasar el array de datos
                          const dataArray = selectedData.map(({ data }) => data);
                          deleteHandler(dataArray);
                        }
                        setSelectedRows(new Set());
                        setSelectedRowForActions(null);
                      }
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (selectedRows.size > 0) {
                      e.target.style.backgroundColor = theme.colors.interactive.danger;
                      e.target.style.color = theme.colors.text.accent;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedRows.size > 0) {
                      e.target.style.backgroundColor = theme.colors.interactive.danger + '20';
                      e.target.style.color = theme.colors.interactive.danger;
                    }
                  }}
                >
                  <Trash2 size={16} />
                  Eliminar
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Filtros */}
      {showFilters && filterable && (
        <div style={{
          padding: theme.spacing.lg,
          borderBottom: `1px solid ${theme.colors.border.primary}`,
          backgroundColor: theme.colors.tertiary,
          display: 'flex',
          flexWrap: 'wrap',
          gap: theme.spacing.md
        }}>
          {columns.filter(col => visibleColumns.has(col.key)).map((column) => (
            <div key={column.key} style={{ minWidth: '200px' }}>
              <label style={{
                display: 'block',
                marginBottom: theme.spacing.xs,
                fontSize: theme.typography.fontSize.xs,
                color: theme.colors.text.secondary,
                fontWeight: theme.typography.fontWeight.medium
              }}>
                {column.title}
              </label>
              <input
                type="text"
                placeholder={`Filtrar ${column.title}...`}
                value={filters[column.key] || ''}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  [column.key]: e.target.value
                }))}
                style={{
                  width: '100%',
                  padding: theme.spacing.sm,
                  backgroundColor: theme.colors.primary,
                  border: `1px solid ${theme.colors.border.primary}`,
                  borderRadius: theme.borderRadius.sm,
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.text.primary,
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = theme.colors.interactive.primary;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = theme.colors.border.primary;
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Tabla con scroll fijo */}
      <div style={{ 
        overflowX: 'auto',
        maxHeight: '600px', // Altura máxima para activar scroll vertical
        position: 'relative'
      }}>
        <table style={tableStyles}>
          <thead style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: theme.colors.tertiary
          }}>
            <tr>
              {/* Checkbox para seleccionar todo */}
              {selectable && (
                <th style={{ ...thStyles, width: '50px' }}>
                  <input
                    type="checkbox"
                    checked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                    onChange={handleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
              )}

              {/* Columnas de datos */}
              {columns.filter(col => visibleColumns.has(col.key)).map((column) => {
                // Debug: Verificar ancho de columnas
                if (column.key === 'mobile') {
                  console.log('🔍 Table2 - Columna mobile width:', column.width, 'columnWidths:', columnWidths[column.key]);
                }
                return (
                <th
                  key={column.key}
                  style={{
                    ...thStyles,
                    width: columnWidths[column.key] || column.width || 'auto',
                    cursor: sortable ? 'pointer' : 'default',
                    position: 'relative',
                    textAlign: column.align || 'center'
                  }}
                  onClick={() => sortable && handleSort(column.key)}
                  onMouseEnter={(e) => {
                    if (sortable) {
                      e.currentTarget.style.backgroundColor = theme.colors.hover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (sortable) {
                      e.currentTarget.style.backgroundColor = theme.colors.tertiary;
                    }
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: column.align === 'left' ? 'flex-start' : column.align === 'right' ? 'flex-end' : 'center',
                    gap: theme.spacing.xs
                  }}>
                    {column.title || column.label}
                    {getSortIcon(column.key)}
                  </div>
                  
                  {/* Resize handle */}
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      backgroundColor: isResizing && resizingColumn === column.key ? theme.colors.interactive.primary : 'transparent',
                      cursor: 'col-resize',
                      zIndex: 1
                    }}
                    onMouseDown={(e) => handleMouseDown(e, column.key)}
                    onMouseEnter={(e) => {
                      if (!isResizing) {
                        e.target.style.backgroundColor = theme.colors.border.secondary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isResizing) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  />
                </th>
                );
              })}

            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor: index % 2 === 1 ? theme.colors.hover : 'transparent',
                  cursor: onRowClick ? 'pointer' : 'default'
                }}
                onClick={() => onRowClick && onRowClick(row, index)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.active;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = index % 2 === 1 ? theme.colors.hover : 'transparent';
                }}
              >
                {/* Checkbox para seleccionar fila */}
                {selectable && (
                  <td style={tdStyles}>
                    <input
                      type="checkbox"
                      checked={selectedRows.has(index)}
                      onChange={() => handleSelectRow(index)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                )}

                {/* Datos de la fila */}
                {columns.filter(col => visibleColumns.has(col.key)).map((column) => (
                  <td 
                    key={column.key} 
                    style={{
                      ...tdStyles,
                      width: columnWidths[column.key] || column.width || 'auto',
                      textAlign: column.align || 'center'
                    }}
                  >
                    {column.render ? 
                      column.render(row[column.key], row, index) : 
                      row[column.key]
                    }
                  </td>
                ))}

              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación y estadísticas */}
      <div style={{
        padding: theme.spacing.lg,
        borderTop: `1px solid ${theme.colors.border.primary}`,
        backgroundColor: theme.colors.secondary,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing.md
      }}>
        <div style={{
          fontSize: theme.typography.fontSize.sm,
          color: theme.colors.text.secondary,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.md
        }}>
          <span>
            Mostrando {Math.max(0, Math.min(itemsPerPage, processedData.length - (currentPage - 1) * itemsPerPage))} de {processedData.length} resultados
            {selectedRows.size > 0 && (
              <span style={{ marginLeft: theme.spacing.sm, color: theme.colors.interactive.primary }}>
                ({selectedRows.size} seleccionados)
              </span>
            )}
          </span>
          
          {/* Selector de elementos por página */}
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
            <span style={{ fontSize: theme.typography.fontSize.xs }}>Elementos por página:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              style={{
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                backgroundColor: theme.colors.primary,
                border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: theme.borderRadius.sm,
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.text.primary,
                outline: 'none',
                cursor: 'pointer'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.colors.interactive.primary;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = theme.colors.border.primary;
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
            <button
              style={{
                ...buttonStyles,
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            >
              Anterior
            </button>
            
            <span style={{
              padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.text.primary
            }}>
              Página {currentPage} de {totalPages}
            </span>
            
            <button
              style={{
                ...buttonStyles,
                opacity: currentPage === totalPages ? 0.5 : 1,
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* Modal de Importación */}
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={(result) => {
          console.log('Importación completada:', result);
          // Algunos componentes pueden tener función onRefresh para recargar datos
          if (props.onRefresh) {
            props.onRefresh();
          }
        }}
      />
    </div>
  );
};

export default Table2;
